import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import type {
  ActivityListResponse,
  BoardResponse,
  MessageListResponse,
  SSEEvent,
} from "@breeze/shared";
import { getSessionId } from "../session.js";
import { applyCardToBoardCache } from "../cache.js";
import { usePresenceStore } from "./usePresence.js";
import { setView } from "../store/view.js";
import { notify } from "../lib/browserNotify.js";

export function useRealtime(queryClient: QueryClient): void {
  useEffect(() => {
    const selfId = getSessionId();
    const es = new EventSource("/api/events/stream");

    const applyBoard = (ev: SSEEvent): void => {
      if ("originatorSessionId" in ev && ev.originatorSessionId === selfId) return;
      if (ev.type === "card.moved") {
        queryClient
          .getQueryCache()
          .findAll({ queryKey: ["board"] })
          .forEach((query) => {
            queryClient.setQueryData<BoardResponse>(query.queryKey, (prev) => {
              if (!prev) return prev;
              const idx = prev.cards.findIndex((c) => c.cardId === ev.cardId);
              if (idx === -1) return prev;
              const existing = prev.cards[idx];
              if (!existing) return prev;
              const cards = [...prev.cards];
              cards[idx] = { ...existing, stage: ev.toStage, position: ev.position };
              return { ...prev, cards };
            });
          });
      } else if (ev.type === "card.updated" || ev.type === "card.created") {
        applyCardToBoardCache(queryClient, ev.card);
        // Cross-tab Visits-tab refresh: when a quote save (or any card
        // update) lands on this card, invalidate the cluster cache so an
        // open Slideout's Visits tab refetches the new visit data.
        if (ev.type === "card.updated") {
          queryClient.invalidateQueries({
            queryKey: ["cluster", ev.card.cardId],
          });
        }
      }
    };

    const applyReadState = (ev: SSEEvent): void => {
      if (ev.type !== "card.read_state") return;
      // Team-shared read-state: any staff read/unread patches every
      // connected client's board cache with the same timestamps. This is
      // what clears the flash on Andzelika's board when Darren opens a
      // card (and resurfaces it for everyone when either hits Mark unread).
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ["board"] })
        .forEach((query) => {
          queryClient.setQueryData<BoardResponse>(query.queryKey, (prev) => {
            if (!prev) return prev;
            const cards = prev.cards.map((c) =>
              c.cardId === ev.cardId
                ? { ...c, lastReadAt: ev.lastReadAt, flaggedAt: ev.flaggedAt }
                : c
            );
            return { ...prev, cards };
          });
        });
    };

    const applyPresence = (ev: SSEEvent): void => {
      if (ev.type !== "card.presence") return;
      usePresenceStore.getState().set(ev.cardId, ev.viewers);
    };

    const applyActivity = (ev: SSEEvent): void => {
      if (ev.type !== "card.activity") return;
      const a = ev.activity;
      queryClient.setQueryData<ActivityListResponse>(
        ["activity", a.cardId],
        (prev) => {
          if (!prev) return { activity: [a] };
          if (prev.activity.some((x) => x.activityId === a.activityId))
            return prev;
          return { activity: [a, ...prev.activity] };
        }
      );
    };

    const applyMessage = (ev: SSEEvent): void => {
      if (ev.type === "message.received") {
        const msg = ev.message;
        if (msg.cardId === null) return;
        queryClient.setQueryData<MessageListResponse>(
          ["messages", msg.cardId],
          (prev) => {
            if (!prev) return { messages: [msg] };
            if (prev.messages.some((m) => m.msgId === msg.msgId)) return prev;
            return { messages: [msg, ...prev.messages] };
          }
        );
      } else if (ev.type === "message.updated") {
        // Same cache-shape update as received, but replace in place if we
        // already have the row (happens when attachments land after ingest).
        const msg = ev.message;
        if (msg.cardId === null) return;
        queryClient.setQueryData<MessageListResponse>(
          ["messages", msg.cardId],
          (prev) => {
            if (!prev) return { messages: [msg] };
            const idx = prev.messages.findIndex((m) => m.msgId === msg.msgId);
            if (idx === -1) return { messages: [msg, ...prev.messages] };
            const next = [...prev.messages];
            next[idx] = msg;
            return { messages: next };
          }
        );
      } else if (ev.type === "message.status_updated") {
        queryClient
          .getQueryCache()
          .findAll({ queryKey: ["messages"] })
          .forEach((query) => {
            const data = query.state.data as MessageListResponse | undefined;
            if (!data) return;
            const idx = data.messages.findIndex((m) => m.msgId === ev.msgId);
            if (idx === -1) return;
            const next = [...data.messages];
            const cur = next[idx];
            if (!cur) return;
            next[idx] = {
              ...cur,
              status: ev.status,
              externalId: ev.externalId ?? cur.externalId,
              errorMessage: ev.errorMessage ?? cur.errorMessage,
            };
            queryClient.setQueryData(query.queryKey, { messages: next });
          });
      }
    };

    const applyCoverage = (ev: SSEEvent): void => {
      if (ev.type !== "technician.idle_changed") return;
      // Invalidate every coverage query — summary and idle list both
      // need to refresh, plus any custom keys callers may add later.
      void queryClient.invalidateQueries({ queryKey: ["coverage"] });
      // Fire a foreground browser notification IF the user has opted in
      // AND the tab is hidden. Suppressed otherwise (no double-flash).
      if (ev.isIdle) {
        const days = ev.sinceDays === null ? "unknown" : `${ev.sinceDays} day${ev.sinceDays === 1 ? "" : "s"}`;
        notify(`${ev.nickname} looks idle`, {
          body: `No work in ${days} and nothing booked. Worth a check.`,
          tag: `coverage-idle-${ev.technicianId}`,
          onClick: () => setView("coverage"),
        });
      } else {
        notify(`${ev.nickname} is back at work`, {
          body: "Idle alert cleared.",
          tag: `coverage-back-${ev.technicianId}`,
          onClick: () => setView("coverage"),
        });
      }
    };

    const listener = (raw: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(raw.data) as SSEEvent;
        applyBoard(parsed);
        applyMessage(parsed);
        applyActivity(parsed);
        applyPresence(parsed);
        applyReadState(parsed);
        applyCoverage(parsed);
      } catch {
        /* malformed event; ignore */
      }
    };

    const types = [
      "card.moved",
      "card.updated",
      "card.created",
      "card.activity",
      "card.presence",
      "card.read_state",
      "message.received",
      "message.updated",
      "message.status_updated",
      "technician.idle_changed",
    ];
    for (const t of types) es.addEventListener(t, listener as EventListener);

    return (): void => {
      es.close();
    };
  }, [queryClient]);
}
